import { type ClassValue, clsx } from "clsx"
import { twMerge } from "tailwind-merge"

import { isDemo } from "~/utils/environment"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/**
 * Normalize a search/query value into a string array. A single `?key=value` parses to a string
 * (not an array), so consumers reading raw search params need this to avoid runtime type errors.
 */
export function toStringArray(value: unknown): string[] | undefined {
  if (value == null) return undefined
  if (Array.isArray(value)) return value as string[]
  if (typeof value === "string") return [value]
  return undefined
}

export const roundNumber = (num: number, digits: number) => {
  const factor = Math.pow(10, digits)
  return Math.round(num * factor) / factor
}

export const toPercent = (num: number, digits: number = 0) => {
  return `${roundNumber(num * 100, digits)}%`
}

export function isNullOrUndefined(value: any) {
  return value === undefined || value === null
}

export function mapPop<T, K>(map: Map<T, K>, key: T): K | undefined {
  const value = map.get(key)
  map.delete(key)
  return value
}

export function removeUndefinedValues<T>(obj: T): T {
  const out = { ...obj }
  for (const key in out) {
    if (out[key] === undefined) {
      delete out[key]
    }
  }
  return out
}

interface Omit {
  <T extends object, K extends [...(keyof T)[]]>(
    obj: T,
    ...keys: K
  ): {
    [K2 in Exclude<keyof T, K[number]>]: T[K2]
  }
}

// Some of the type inference is not perfect with lodash's omit
// so I stole this from https://stackoverflow.com/a/53968837/5371102
export const omit: Omit = (obj, ...keys) => {
  const ret = {} as {
    [K in keyof typeof obj]: (typeof obj)[K]
  }
  let key: keyof typeof obj
  for (key in obj) {
    if (!keys.includes(key)) {
      ret[key] = obj[key]
    }
  }
  return ret
}

export function stringToNumber(value: string) {
  // takes any string and converts it to a number based on the
  // char codes of the characters in the string
  let num = 0
  const MAX_NUMBER = 123143597 // Not a special number, you can make it longer to make a larger average ID
  // but if you do you will risk more sku's with different ID's have the same ID.
  for (let i = 0; i < value.length; i++) {
    // uses a multiplicative hash functions with all components being relative prime to minimize the probablity
    // of sku's with different ID gets mapped to the same id
    num = (num * 324331 + value.charCodeAt(i) * 234321543) % MAX_NUMBER
  }
  return num
}

export function formatSkuId(skuId: string | undefined) {
  if (!skuId) {
    return ""
  }

  if (isDemo()) {
    return `skuId ${stringToNumber(skuId)}`
  } else {
    return skuId
  }
}

export function formatVariantId(variantId: string | undefined) {
  if (!variantId) {
    return ""
  }

  if (isDemo()) {
    return `variantId ${stringToNumber(variantId)}`
  } else {
    return variantId
  }
}

export function formatProductId(productId: string | undefined) {
  if (!productId) {
    return ""
  }

  if (isDemo()) {
    return `productId ${stringToNumber(productId)}`
  } else {
    return productId
  }
}

export function sanitizeName(name: string | undefined): string {
  if (!name) {
    return ""
  }

  if (isDemo()) {
    return `name ${stringToNumber(name)}`
  } else {
    return name
  }
}

export function inMemoryCache<T extends (...args: any[]) => any>(fn: T): T {
  // If used in the context of clousures this want work. If the function is
  // recreated each time the cache is reset.
  const cache = new Map<string, any>()

  return function (...args: any[]) {
    if (args.some((arg) => typeof arg === "function")) {
      console.warn("Cannot cache function results with function arguments.")
      // Do not cache if any argument is a function
      return fn(...args)
    }
    const key = JSON.stringify(args)
    if (cache.has(key)) return cache.get(key)

    const result = fn(...args)
    cache.set(key, result)
    return result
  } as T
}

export const isInDialog = (target: EventTarget | null) =>
  target instanceof Element && target.closest('[role="dialog"], [role="alertdialog"]') != null

/** A Radix modal (⌘K included) is open over the page; popovers like a pill menu don't count */
export const isModalOpen = () =>
  [...document.querySelectorAll('[role="dialog"][data-state="open"]')].some(
    (el) => !el.closest("[data-radix-popper-content-wrapper]"),
  )

/**
 * Whether focus sits in a text-entry element. Keyboard shortcuts check this so typing in an input,
 * a textarea or a contenteditable never triggers them.
 */
export function isEditableElement(el: Element | null): boolean {
  if (!el) return false
  if (el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement) return true
  if (el instanceof HTMLElement && el.isContentEditable) return true
  if (el instanceof HTMLSelectElement) return true
  // An ag-grid cell being edited: the focused node can be the cell wrapper rather than the
  // editor's input, and an unmodified single-key shortcut would otherwise steal the keystroke.
  if (el.closest?.(".ag-cell-inline-editing, .ag-cell-editor, .ag-popup-editor")) return true
  return false
}
